import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import moment from "moment";
import {
    Alert,
    Box,
    Button,
    Checkbox,
    CircularProgress,
    Divider,
    FormControlLabel,
    Link,
    Stack,
    TextField,
    Typography,
} from "@mui/material";

import { AppModuleLayout } from "@layouts";
import { ICustomerQuote } from "@interfaces";
import {
    canProceedToCheckout,
    canSaveComment,
    classifyQuoteLoadError,
    customerQuoteOwing,
    customerQuoteTotal,
    IQuoteLoadError,
    summariseCustomerQuoteItems,
} from "src/Utilities/customerQuoteHelpers";
import { PATH_TERM } from "@routes";
import {
    fetchCustomerQuote,
    persistCustomerComment,
    startCustomerQuotePayment,
} from "./customerQuote";

const formatMoney = (value: number): string => `$${(Number(value) || 0).toFixed(2)}`;

const formatDate = (date: string): string => {
    if (!date) return "";
    const m = moment(date, ["DD-MMM-YYYY", "YYYY-MM-DD", moment.ISO_8601]);
    return m.isValid() ? m.format("ddd, DD MMM YYYY") : date;
};

export const CustomerQuote = () => {
    const { quoteId, quoteToken, randomString } = useParams<{
        quoteId: string;
        quoteToken: string;
        randomString: string;
    }>();

    const [quote, setQuote] = useState<ICustomerQuote | null>(null);
    const [loadError, setLoadError] = useState<IQuoteLoadError | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    const [comment, setComment] = useState("");
    const [isSavingComment, setIsSavingComment] = useState(false);
    const [commentSaved, setCommentSaved] = useState(false);

    const [isCheckingOut, setIsCheckingOut] = useState(false);
    const [actionError, setActionError] = useState<string | null>(null);
    const [termsAccepted, setTermsAccepted] = useState(false);
    const [paymentSettled, setPaymentSettled] = useState(false);

    useEffect(() => {
        let active = true;
        (async () => {
            if (!quoteId || !quoteToken || !randomString) {
                setLoadError(classifyQuoteLoadError({ response: { status: 404 } }));
                setIsLoading(false);
                return;
            }
            setIsLoading(true);
            setLoadError(null);
            try {
                const data = await fetchCustomerQuote(quoteId, quoteToken, randomString);
                if (!active) return;
                setQuote(data);
                setComment(data.customer_comment ?? "");
            } catch (err) {
                if (!active) return;
                setLoadError(classifyQuoteLoadError(err));
            } finally {
                if (active) setIsLoading(false);
            }
        })();
        return () => {
            active = false;
        };
    }, [quoteId, quoteToken, randomString]);

    const digests = useMemo(() => (quote ? summariseCustomerQuoteItems(quote) : []), [quote]);
    const total = useMemo(() => (quote ? customerQuoteTotal(quote) : 0), [quote]);

    const handleSaveComment = async () => {
        if (!quoteId || !quoteToken || !randomString || !canSaveComment(comment)) return;
        setIsSavingComment(true);
        setCommentSaved(false);
        setActionError(null);
        try {
            await persistCustomerComment(quoteId, quoteToken, randomString, comment);
            setCommentSaved(true);
        } catch {
            setActionError("Couldn't save your comment. Please try again.");
        } finally {
            setIsSavingComment(false);
        }
    };

    const handleCheckout = async () => {
        if (!quoteId || !quoteToken || !randomString) return;
        if (!quote || !canProceedToCheckout(termsAccepted, quote.ordersProducts.length)) return;
        setIsCheckingOut(true);
        setActionError(null);
        try {
            const owingBalance = customerQuoteOwing(quote);
            const result = await startCustomerQuotePayment(
                quoteId,
                quoteToken,
                randomString,
                owingBalance,
            );
            if (result.kind === "redirect") {
                window.location.href = result.redirectUrl;
                return;
            }
            if (result.kind === "balance_mismatch") {
                // Backend reported nothing owed while the quote still shows a
                // balance — don't claim the booking is paid. Ask the customer
                // to get in touch instead of misleading them.
                setActionError(
                    "This booking still has an outstanding balance, but we couldn't start the payment right now. Please contact us so we can help complete it.",
                );
                setIsCheckingOut(false);
                return;
            }
            // Nothing owed — the booking is already settled. Confirm instead of
            // sending the customer to an empty payment page.
            setPaymentSettled(true);
            setIsCheckingOut(false);
        } catch {
            setActionError("Couldn't start checkout. Please try again.");
            setIsCheckingOut(false);
        }
    };

    if (isLoading) {
        return (
            <AppModuleLayout>
                <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}>
                    <CircularProgress />
                </Box>
            </AppModuleLayout>
        );
    }

    if (loadError) {
        return (
            <AppModuleLayout>
                <Box sx={{ maxWidth: 720, mx: "auto", px: 2, py: 6 }}>
                    <Alert severity={loadError.kind === "error" ? "error" : "warning"}>
                        {loadError.message}
                    </Alert>
                </Box>
            </AppModuleLayout>
        );
    }

    if (!quote) return null;

    return (
        <AppModuleLayout>
        <Box sx={{ maxWidth: 720, mx: "auto", px: 2, py: 4 }}>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 0.5 }}>
                {quote.bookingReference ? `Quote ${quote.bookingReference}` : "Your Quote"}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Review your quote below, leave a comment if you'd like, then proceed to checkout.
            </Typography>

            <Stack spacing={2}>
                {digests.map((item, index) => (
                    <Box
                        key={`${item.productId}-${index}`}
                        sx={{
                            border: "1px solid",
                            borderColor: "divider",
                            borderRadius: 2,
                            p: 2,
                        }}
                    >
                        <Stack
                            direction="row"
                            justifyContent="space-between"
                            alignItems="flex-start"
                            spacing={2}
                        >
                            <Box>
                                <Typography sx={{ fontWeight: 600 }}>{item.name}</Typography>
                                {item.fareName ? (
                                    <Typography variant="body2" color="text.secondary">
                                        {item.fareName}
                                    </Typography>
                                ) : null}
                                <Typography variant="body2" color="text.secondary">
                                    {formatDate(item.startDate)}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Qty: {item.quantity}
                                </Typography>
                                {item.travellers && item.travellers.length > 0 ? (
                                    <Typography variant="body2" color="text.secondary">
                                        Travellers: {item.travellers.join(", ")}
                                    </Typography>
                                ) : null}
                                {item.pickupLocation || item.pickupTime ? (
                                    <Typography variant="body2" color="text.secondary">
                                        Pickup:{" "}
                                        {[item.pickupTime, item.pickupLocation]
                                            .filter(Boolean)
                                            .join(" — ")}
                                    </Typography>
                                ) : null}
                                {item.dropoffLocation || item.dropoffTime ? (
                                    <Typography variant="body2" color="text.secondary">
                                        Drop-off:{" "}
                                        {[item.dropoffTime, item.dropoffLocation]
                                            .filter(Boolean)
                                            .join(" — ")}
                                    </Typography>
                                ) : null}
                                {item.voucher ? (
                                    <Typography variant="body2" color="text.secondary">
                                        Voucher: {item.voucher}
                                    </Typography>
                                ) : null}
                                {item.supplierName ? (
                                    <Typography variant="body2" color="text.secondary">
                                        Supplier: {item.supplierName}
                                        {item.supplierPhone ? ` · ${item.supplierPhone}` : ""}
                                        {item.supplierEmail ? ` · ${item.supplierEmail}` : ""}
                                    </Typography>
                                ) : null}
                                {item.bookingComment ? (
                                    <Typography variant="body2" color="text.secondary">
                                        Note: {item.bookingComment}
                                    </Typography>
                                ) : null}
                                {item.supplierComments ? (
                                    <Typography variant="body2" color="text.secondary">
                                        Supplier note: {item.supplierComments}
                                    </Typography>
                                ) : null}
                            </Box>
                            <Typography sx={{ fontWeight: 600, whiteSpace: "nowrap" }}>
                                {formatMoney(item.lineTotal)}
                            </Typography>
                        </Stack>
                    </Box>
                ))}
            </Stack>

            <Divider sx={{ my: 3 }} />

            <Stack direction="row" justifyContent="space-between" sx={{ mb: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Total
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {formatMoney(total)}
                </Typography>
            </Stack>

            <Box sx={{ mb: 3 }}>
                <Typography sx={{ fontWeight: 600, mb: 1 }}>Add a comment</Typography>
                <TextField
                    fullWidth
                    multiline
                    minRows={3}
                    placeholder="Any notes or requests for this quote?"
                    value={comment}
                    onChange={(e) => {
                        setComment(e.target.value);
                        setCommentSaved(false);
                    }}
                />
                <Stack direction="row" alignItems="center" spacing={2} sx={{ mt: 1 }}>
                    <Button
                        variant="outlined"
                        onClick={handleSaveComment}
                        disabled={isSavingComment || !canSaveComment(comment)}
                    >
                        {isSavingComment ? "Saving..." : "Save comment"}
                    </Button>
                    {commentSaved ? (
                        <Typography variant="body2" color="success.main">
                            Comment saved
                        </Typography>
                    ) : null}
                </Stack>
            </Box>

            {actionError ? (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {actionError}
                </Alert>
            ) : null}

            <FormControlLabel
                sx={{ mb: 2, alignItems: "flex-start" }}
                control={
                    <Checkbox
                        checked={termsAccepted}
                        onChange={(e) => setTermsAccepted(e.target.checked)}
                        sx={{ pt: 0 }}
                    />
                }
                label={
                    <Typography variant="body2" color="text.secondary">
                        I have read and agree to the{" "}
                        <Link href={PATH_TERM} target="_blank" rel="noopener noreferrer">
                            Terms and Conditions
                        </Link>
                        .
                    </Typography>
                }
            />

            {paymentSettled ? (
                <Alert severity="success" sx={{ mb: 2 }}>
                    This booking has no outstanding balance — it&apos;s already paid.
                    {quote.bookingReference
                        ? ` Reference: ${quote.bookingReference}.`
                        : ""}
                </Alert>
            ) : (
                <Button
                    fullWidth
                    variant="contained"
                    size="large"
                    onClick={handleCheckout}
                    disabled={
                        isCheckingOut ||
                        !canProceedToCheckout(termsAccepted, quote.ordersProducts.length)
                    }
                >
                    {isCheckingOut ? "Preparing checkout..." : "Proceed to checkout"}
                </Button>
            )}
        </Box>
        </AppModuleLayout>
    );
};

export default CustomerQuote;
